import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { DailyWatchChart } from '../components/DailyWatchChart';
import { formatDurationSeconds, formatTimeRemaining, getWatchProgressByVideoId } from '@kidstube/shared';
import type { SettingsExport, WatchHistoryEntry } from '@kidstube/shared';

const PARENT_DASHBOARD_REFRESH_INTERVAL_MS = 5 * 60_000;

function mergeWatchHistoryByVideo(history: WatchHistoryEntry[]): WatchHistoryEntry[] {
    const byVideoId = new Map<string, WatchHistoryEntry>();

    for (const entry of history) {
        const key = `${entry.profileId}:${entry.videoId}`;
        const existing = byVideoId.get(key);

        if (!existing) {
            byVideoId.set(key, entry);
            continue;
        }

        const entryWatchedAt = Date.parse(entry.watchedAt);
        const existingWatchedAt = Date.parse(existing.watchedAt);
        const newerEntry = entryWatchedAt > existingWatchedAt ? entry : existing;

        byVideoId.set(key, {
            ...newerEntry,
            watchedDurationSeconds: Math.max(existing.watchedDurationSeconds || 0, entry.watchedDurationSeconds || 0),
            totalDurationSeconds: Math.max(existing.totalDurationSeconds || 0, entry.totalDurationSeconds || 0),
            watchedAt: newerEntry.watchedAt
        });
    }

    return [...byVideoId.values()].sort((a, b) => new Date(b.watchedAt).getTime() - new Date(a.watchedAt).getTime());
}

export function ParentDashboard() {
    const [pin, setPin] = useState('');
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [, setTick] = useState(0);
    const navigate = useNavigate();
    const [cloudProgress, setCloudProgress] = useState<WatchHistoryEntry[]>([]);
    const [dailyWatchTotals, setDailyWatchTotals] = useState<Array<{ date: string; seconds: number }>>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const historyRequestInFlightRef = useRef(false);
    const { parentSettings, unlockWithPin, profiles, currentProfile, watchHistory, allowlist, denylist, importSettings, resetSession, sessionDurationMinutes, getRemainingSeconds, hasActiveSession, removeFromAllowlist, removeFromDenylist } = useAppStore();

    // Force re-render every second for live timer display
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    const remainingSeconds = getRemainingSeconds();

    const handleUnlock = async (e: React.FormEvent) => {
        e.preventDefault(); setError('');
        if (!parentSettings && pin.length >= 4) {
            await unlockWithPin(pin);
            setIsUnlocked(true);
        } else {
            const success = await unlockWithPin(pin);
            if (success) {
                setIsUnlocked(true);
            } else {
                setError('Incorrect PIN');
                setPin('');
            }
        }
    };

    const loadCloudHistory = useCallback(async (showLoading = false) => {
        if (historyRequestInFlightRef.current) return;
        historyRequestInFlightRef.current = true;
        if (showLoading) setHistoryLoading(true);
        const profileId = currentProfile?.id || 'default';
        try {
            const response = await fetch(
                `/api/watch-history?view=dashboard&limit=50&profileId=${encodeURIComponent(profileId)}&timezoneOffsetMinutes=${new Date().getTimezoneOffset()}`,
                { credentials: 'include', cache: 'no-store' }
            );
            if (!response.ok) throw new Error(`History refresh failed with ${response.status}`);
            const data = await response.json();
            const mapCloudItems = (items: any[]): WatchHistoryEntry[] => items.map((item: any) => ({
                profileId: item.profileId || profileId,
                videoId: item.videoId,
                videoTitle: item.videoTitle,
                channelId: item.channelId || undefined,
                channelTitle: item.channelTitle,
                thumbnailUrl: item.thumbnailUrl,
                watchedDurationSeconds: item.watchedDurationSeconds || 0,
                totalDurationSeconds: item.totalDurationSeconds || 0,
                lastPositionSeconds: item.lastPositionSeconds || 0,
                watchedAt: item.watchedAt || item.updatedAt || new Date().toISOString()
            }));

            setCloudProgress(mapCloudItems(data.progress || []));
            setDailyWatchTotals((data.dailyTotals || []).map((day: any) => ({
                date: String(day.date || ''),
                seconds: Math.max(0, Math.floor(Number(day.seconds) || 0))
            })).filter((day: { date: string }) => day.date.length > 0));
        } catch {
            // Keep the last successful cloud snapshot visible and retry on the
            // next poll/focus instead of replacing it with an empty dashboard.
        } finally {
            historyRequestInFlightRef.current = false;
            if (showLoading) setHistoryLoading(false);
        }
    }, [currentProfile?.id]);

    // Focus is immediate; the slow fallback poll keeps a continuously visible
    // parent portal current without repeatedly scanning D1.
    useEffect(() => {
        if (!isUnlocked) return;
        void loadCloudHistory(true);
        const refreshWhenVisible = () => {
            if (document.visibilityState !== 'hidden') void loadCloudHistory(false);
        };
        const interval = window.setInterval(refreshWhenVisible, PARENT_DASHBOARD_REFRESH_INTERVAL_MS);
        window.addEventListener('focus', refreshWhenVisible);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', refreshWhenVisible);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [isUnlocked, loadCloudHistory]);

    const mergedHistory = mergeWatchHistoryByVideo([...watchHistory, ...cloudProgress]);
    const progressByVideoId = getWatchProgressByVideoId(mergedHistory);

    const handleExport = () => {
        if (!parentSettings) return;
        const exportData: SettingsExport = {
            version: 1,
            exportedAt: new Date().toISOString(),
            parentSettings: {
                allowlist,
                denylist,
                profiles,
                defaultProfileId: currentProfile?.id || parentSettings.defaultProfileId,
                createdAt: parentSettings.createdAt,
                updatedAt: parentSettings.updatedAt,
            },
            watchHistory,
        };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kidstube-settings-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setMessage('Settings exported!');
        setTimeout(() => setMessage(''), 3000);
    };

    const handleImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            try {
                const text = await file.text();
                const data = JSON.parse(text) as SettingsExport;
                if (!data.version || !data.parentSettings) throw new Error('Invalid format');
                importSettings(data);
                setMessage('Settings imported!');
                setTimeout(() => setMessage(''), 3000);
            } catch { setError('Failed to import settings'); setTimeout(() => setError(''), 3000); }
        };
        input.click();
    };

    if (!isUnlocked) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem' }}>🔒</div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '1rem' }}>Parent Area</h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '300px' }}>{parentSettings ? 'Enter your PIN' : 'Create a 4-6 digit PIN'}</p>
            <form onSubmit={handleUnlock} style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="Enter PIN" autoFocus style={{ width: '180px', padding: '1rem', fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5em', background: 'rgba(255,255,255,0.08)', border: '2px solid rgba(255,255,255,0.12)', borderRadius: '12px', color: '#fff' }} />
                {error && <p style={{ color: '#ff7675', fontSize: '0.9rem' }}>{error}</p>}
                <button type="submit" disabled={pin.length < 4} style={{ padding: '0.5rem 2rem', background: 'linear-gradient(135deg,#6c5ce7,#5b4cdb)', borderRadius: '9999px', fontWeight: 600 }}>{parentSettings ? 'Unlock' : 'Create PIN'}</button>
            </form>
        </div>
    );

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '2rem' }}>Parent Dashboard</h1>
            {message && <div style={{ padding: '1rem', background: 'rgba(0,184,148,0.2)', border: '1px solid #00b894', borderRadius: '12px', marginBottom: '1rem', textAlign: 'center' }}>{message}</div>}
            {error && <div style={{ padding: '1rem', background: 'rgba(255,118,117,0.2)', border: '1px solid #ff7675', borderRadius: '12px', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(108,92,231,0.15)', border: '1px solid rgba(108,92,231,0.3)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Session Control</h2>
                {hasActiveSession() ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                                Session duration: <strong>{sessionDurationMinutes} min</strong>
                            </p>
                            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
                                Time remaining: <strong>{formatTimeRemaining(remainingSeconds)}</strong>
                            </p>
                        </div>
                        <button
                            onClick={() => { resetSession(); setMessage('Session reset! Child will be asked to set new time.'); }}
                            style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg,#e17055,#d63031)', borderRadius: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                        >
                            🔄 Reset Session
                        </button>
                    </div>
                ) : (
                    <p style={{ color: 'rgba(255,255,255,0.5)' }}>No active session</p>
                )}
            </section>

            <section style={{ marginBottom: '2rem' }}>
                <DailyWatchChart dailyTotals={dailyWatchTotals} />
            </section>

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Profiles</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>{profiles.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.04)', borderRadius: '12px' }}>
                        <span style={{ fontSize: '2rem', width: '50px', height: '50px', background: 'linear-gradient(135deg,#6c5ce7,#fd79a8)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.avatarId}</span>
                        <div><h3 style={{ fontWeight: 600 }}>{p.name}</h3></div>
                    </div>
                ))}</div>
            </section>

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>
                    Watch History {historyLoading && <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>(loading...)</span>}
                </h2>
                {mergedHistory.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No history yet</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto' }}>{mergedHistory.slice(0, 20).map((e) => {
                        const progress = progressByVideoId.get(e.videoId);
                        const percent = progress?.totalDurationSeconds ? Math.round(progress.ratio * 100) : 0;
                        const label = progress?.completed ? 'Watched' : percent > 0 ? `${percent}%` : 'Started';
                        const lastPositionLabel = e.lastPositionSeconds && e.lastPositionSeconds > 0
                            ? `Left at ${formatDurationSeconds(e.lastPositionSeconds)}`
                            : null;
                        const watchedLabel = e.watchedDurationSeconds > 0
                            ? `Watched ${formatDurationSeconds(e.watchedDurationSeconds)}`
                            : null;

                        return (
                            <button
                                key={`${e.profileId}:${e.videoId}`}
                                onClick={() => navigate(`/watch/${e.videoId}`)}
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    padding: '0.5rem',
                                    background: 'rgba(255,255,255,0.04)',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    color: 'inherit',
                                    width: '100%',
                                    transition: 'background 0.2s'
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                                onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                            >
                                <img src={e.thumbnailUrl} alt="" style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '8px' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontSize: '0.9rem', fontWeight: 500, margin: 0 }}>{e.videoTitle}</p>
                                    <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>{e.channelTitle}</p>
                                    {(watchedLabel || lastPositionLabel) && (
                                        <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.48)', margin: '0.15rem 0 0' }}>
                                            {[watchedLabel, lastPositionLabel].filter(Boolean).join(' • ')}
                                        </p>
                                    )}
                                </div>
                                <span style={{
                                    alignSelf: 'center',
                                    flexShrink: 0,
                                    padding: '0.35rem 0.65rem',
                                    borderRadius: '9999px',
                                    background: progress?.completed ? 'rgba(0,184,148,0.22)' : 'rgba(253,203,110,0.2)',
                                    color: progress?.completed ? '#55efc4' : '#fdcb6e',
                                    fontSize: '0.72rem',
                                    fontWeight: 800
                                }}>{label}</span>
                            </button>
                        );
                    })}</div>
                )}
            </section>

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Allowlist ({allowlist.length})</h2>
                {allowlist.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No allowlisted channels/videos</p> :
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{allowlist.map(e => (
                        <span key={e.id} style={{ padding: '0.5rem 1rem', background: 'rgba(0,184,148,0.2)', border: '1px solid #00b894', borderRadius: '9999px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {e.title}
                            <button onClick={() => removeFromAllowlist(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '0.9rem', lineHeight: 1 }} title="Remove">❌</button>
                        </span>
                    ))}</div>
                }
            </section>

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Denylist ({denylist.length})</h2>
                {denylist.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No blocked channels/videos</p> :
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{denylist.map(e => (
                        <span key={e.id} style={{ padding: '0.5rem 1rem', background: 'rgba(255,118,117,0.2)', border: '1px solid #ff7675', borderRadius: '9999px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {e.title}
                            <button onClick={() => removeFromDenylist(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '0.9rem', lineHeight: 1 }} title="Remove">❌</button>
                        </span>
                    ))}</div>
                }
            </section>

            <section style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Family Cloud Account</h2>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
                    This parent-established account keeps profiles, blocks, likes, time settings, and watch progress synchronized across devices.
                </p>
                <GoogleSignIn />
            </section>

            <section style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>Settings Backup</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button onClick={handleExport} style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', fontWeight: 500 }}>📤 Export Settings</button>
                    <button onClick={handleImport} style={{ padding: '0.75rem 1.5rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', fontWeight: 500 }}>📥 Import Settings</button>
                </div>
            </section>
        </div>
    );
}
